import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection, getDocs, addDoc, updateDoc, doc,
  orderBy, query, serverTimestamp, getDoc, where
} from 'firebase/firestore';
import { useColeccion, BarraActualizacion, useEstadoPersistente, usePosicionScroll, useBorrador, useDescartarBorrador, useHayBorrador, useBorradorPendiente, AvisoBorrador } from './DatosContext';
import { BotonSolicitarEliminacion, AvisoSolicitudEliminacion } from './SolicitudEliminacion';
import DialogoConfirmar from './Dialogo';
import { ejecutarEliminacion } from './operaciones';

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');


// ─── HELPERS ─────────────────────────────────────────────────────────────────

function formatearFechaCorta(fecha) {
  if (!fecha) return '—';
  const d = fecha.toDate ? fecha.toDate() : new Date(fecha);
  const meses = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  return `${d.getDate()} ${meses[d.getMonth()]} ${d.getFullYear()}`;
}

function formatearEuros(valor) {
  if (!valor && valor !== 0) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(valor);
}

function formatearPct(n) {
  if (n === null || n === undefined) return '—';
  return `${Number(n).toFixed(1)}%`;
}

function colorMargen(pct) {
  if (pct === null || pct === undefined) return '#AFA9EC';
  if (pct >= 50) return '#1D9E75';
  if (pct >= 35) return '#BA7517';
  return '#D85A30';
}

function validateEmail(email) {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function formulariosIguales(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

// ─── COMPONENTES COMPARTIDOS ──────────────────────────────────────────────────

function ModalConfirmar({ titulo, mensaje, onConfirmar, onCancelar, textoCancelar, textoConfirmar }) {
  // Sesión 34: todos los avisos usan el diálogo común (la opción segura es la destacada; Enter/Esc la eligen)
  const esBorrado = /eliminar|eliminando/i.test(textoConfirmar || '');
  return (
    <DialogoConfirmar
      titulo={titulo || 'Cambios sin guardar'}
      mensaje={mensaje}
      textoSeguro={textoCancelar && textoCancelar !== 'Cancelar' ? textoCancelar : (esBorrado ? 'No eliminar' : 'Seguir editando')}
      textoPeligroso={textoConfirmar || 'Salir sin guardar'}
      onSeguro={onCancelar}
      onPeligroso={onConfirmar}
      ocupado={/^eliminando/i.test(textoConfirmar || '')}
    />
  );
}

function estiloInput(error) {
  return {
    background: '#0e0b24',
    border: `1px solid ${error ? '#D85A30' : '#534AB7'}`,
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#ffffff',
    fontSize: '0.9rem',
    width: '100%',
    outline: 'none',
    fontFamily: 'DM Sans, sans-serif',
    colorScheme: 'dark',
    boxSizing: 'border-box',
  };
}

function Campo({ label, children, error, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', ...style }}>
      <label style={styles.label}>{label}</label>
      {children}
      {error && <span style={{ color: '#D85A30', fontSize: '0.78rem' }}>{error}</span>}
    </div>
  );
}

function DatoFila({ label, valor }) {
  return (
    <div style={styles.datoFila}>
      <span style={styles.datoLabel}>{label}</span>
      <span style={styles.datoValor}>{valor}</span>
    </div>
  );
}

// ─── BLOQUE DE CONTACTO (reutilizable) ───────────────────────────────────────
// conMobil = true incluye campo móvil

function BloqueContacto({ prefijo, form, set, errores, conMobil = true }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Fila 1: Nombre · Cargo · Móvil (opcional) · Teléfono fijo */}
      <div style={{ display: 'grid', gridTemplateColumns: conMobil ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)', gap: '12px' }}>
        <Campo label="Nombre">
          <input style={estiloInput()} value={form[`${prefijo}_nombre`]} onChange={e => set(`${prefijo}_nombre`, e.target.value)} />
        </Campo>
        <Campo label="Cargo">
          <input style={estiloInput()} value={form[`${prefijo}_cargo`]} onChange={e => set(`${prefijo}_cargo`, e.target.value)} />
        </Campo>
        {conMobil && (
          <Campo label="Móvil">
            <input type="tel" style={estiloInput()} value={form[`${prefijo}_movil`]} onChange={e => set(`${prefijo}_movil`, e.target.value)} />
          </Campo>
        )}
        <Campo label="Teléfono fijo">
          <input type="tel" style={estiloInput()} value={form[`${prefijo}_telefono`]} onChange={e => set(`${prefijo}_telefono`, e.target.value)} />
        </Campo>
      </div>
      {/* Fila 2: Email — línea completa */}
      <Campo label="Email" error={errores[`${prefijo}_email`]}>
        <input type="email" style={estiloInput(errores[`${prefijo}_email`])} value={form[`${prefijo}_email`]} onChange={e => set(`${prefijo}_email`, e.target.value)} />
      </Campo>
    </div>
  );
}

// ─── ESTADO VACÍO ─────────────────────────────────────────────────────────────

const emptyForm = () => ({
  tipo: 'empresa',
  // Empresa
  razon_social: '',
  nombre_comercial: '',
  // Particular
  nombre: '',
  apellidos: '',
  // Compartidos
  nif: '',
  // Particular: contacto directo
  movil: '',
  telefono: '',
  email: '',
  // Dirección fiscal
  direccion: '',
  cp: '',
  localidad: '',
  provincia: '',
  pais: 'España',
  // Contacto principal (solo empresa)
  c1_nombre: '',
  c1_cargo: '',
  c1_movil: '',
  c1_telefono: '',
  c1_email: '',
  // Contacto administración (empresa y particular)
  c2_nombre: '',
  c2_cargo: '',
  c2_movil: '',
  c2_telefono: '',
  c2_email: '',
  // Notas
  notas_iniciales: '',
  forma_pago: '',
  banco: '',
  iban: '',
});

// ─── FORMULARIO ───────────────────────────────────────────────────────────────

function FormularioCliente({ inicial, onGuardar, onCancelar, titulo, claveBorrador = null }) {
  const [estadoInicial] = useState(() => inicial || emptyForm());
  // Sesión 34: borrador — lo escrito se conserva si sales a otro módulo sin guardar
  const [form, setForm, recuperado] = useBorrador(claveBorrador, estadoInicial);
  const [avisoBorrador, setAvisoBorrador] = useState(recuperado);
  const descartarBorrador = useDescartarBorrador();
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [modal, setModal] = useState(false);

  const set = (campo, valor) => {
    setForm(f => ({ ...f, [campo]: valor }));
    setErrores(e => ({ ...e, [campo]: undefined }));
  };

  const hayDatos = !formulariosIguales(form, estadoInicial);

  const intentarCancelar = () => {
    if (hayDatos) setModal(true);
    else onCancelar();
  };

  const validar = () => {
    const e = {};
    if (form.tipo === 'empresa' && !form.razon_social.trim()) e.razon_social = 'La razón social es obligatoria';
    if (form.tipo === 'particular' && !form.nombre.trim()) e.nombre = 'El nombre es obligatorio';
    if (form.tipo === 'particular' && form.email && !validateEmail(form.email)) e.email = 'Email no válido';
    if (form.c1_email && !validateEmail(form.c1_email)) e.c1_email = 'Email no válido';
    if (form.c2_email && !validateEmail(form.c2_email)) e.c2_email = 'Email no válido';
    return e;
  };

  const handleGuardar = async () => {
    const e = validar();
    if (Object.keys(e).length > 0) { setErrores(e); return; }
    setGuardando(true);
    await onGuardar(form);
    descartarBorrador(claveBorrador);
    setGuardando(false);
  };

  return (
    <div style={{ ...styles.container, paddingBottom: '100px' }}>
      {modal && (
        <ModalConfirmar
          mensaje="¿Quieres salir sin guardar este cliente?"
          onConfirmar={() => { descartarBorrador(claveBorrador); onCancelar(); }}
          onCancelar={() => setModal(false)}
        />
      )}

      <div style={styles.cabecera}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button style={styles.botonVolver} onClick={intentarCancelar}>← Clientes</button>
          <h1 style={styles.titulo}>{titulo}</h1>
        </div>
      </div>

      {avisoBorrador && (
        <div style={{ padding: '16px 32px 0' }}>
          <AvisoBorrador onDescartar={() => { setForm(estadoInicial); setAvisoBorrador(false); }} />
        </div>
      )}

      {/* TIPO */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Tipo de cliente</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          {[{ v: 'empresa', l: 'Empresa / Sociedad' }, { v: 'particular', l: 'Particular' }].map(({ v, l }) => (
            <button key={v} onClick={() => set('tipo', v)} style={{
              ...styles.pill,
              background: form.tipo === v ? '#534AB7' : 'transparent',
              color: form.tipo === v ? '#fff' : '#AFA9EC',
              borderColor: form.tipo === v ? '#534AB7' : 'rgba(83,74,183,0.3)',
              padding: '8px 20px', fontSize: '0.88rem',
            }}>{l}</button>
          ))}
        </div>
      </div>

      {/* DATOS PRINCIPALES */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>
          {form.tipo === 'empresa' ? 'Datos de la empresa' : 'Datos del cliente'}
        </h2>

        {form.tipo === 'empresa' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Fila 1: Razón social · CIF */}
            <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '2fr 1fr', gap: '12px' }}>
              <Campo label="Razón social *" error={errores.razon_social}>
                <input style={estiloInput(errores.razon_social)} value={form.razon_social} onChange={e => set('razon_social', e.target.value)} />
              </Campo>
              <Campo label="CIF">
                <input style={estiloInput()} value={form.nif} onChange={e => set('nif', e.target.value)} />
              </Campo>
            </div>
            {/* Fila 1b: Nombre comercial */}
            <Campo label="Nombre comercial">
              <input style={estiloInput()} value={form.nombre_comercial || ''} onChange={e => set('nombre_comercial', e.target.value)} placeholder="Nombre con el que se conoce al cliente (si es diferente)" />
            </Campo>
            {/* Fila 2: Dirección */}
            <Campo label="Dirección">
              <input style={estiloInput()} value={form.direccion} onChange={e => set('direccion', e.target.value)} />
            </Campo>
            {/* Fila 3: CP · Localidad · Provincia · País */}
            <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr 1fr' : '100px 1fr 1fr 1fr', gap: '12px' }}>
              <Campo label="CP">
                <input style={estiloInput()} value={form.cp} onChange={e => set('cp', e.target.value)} />
              </Campo>
              <Campo label="Localidad">
                <input style={estiloInput()} value={form.localidad} onChange={e => set('localidad', e.target.value)} />
              </Campo>
              <Campo label="Provincia">
                <input style={estiloInput()} value={form.provincia} onChange={e => set('provincia', e.target.value)} />
              </Campo>
              <Campo label="País">
                <input style={estiloInput()} value={form.pais} onChange={e => set('pais', e.target.value)} />
              </Campo>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Fila 1: Nombre · Apellidos · NIF */}
            <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr 120px', gap: '12px' }}>
              <Campo label="Nombre *" error={errores.nombre}>
                <input style={estiloInput(errores.nombre)} value={form.nombre} onChange={e => set('nombre', e.target.value)} />
              </Campo>
              <Campo label="Apellidos">
                <input style={estiloInput()} value={form.apellidos} onChange={e => set('apellidos', e.target.value)} />
              </Campo>
              <Campo label="NIF / DNI">
                <input style={estiloInput()} value={form.nif} onChange={e => set('nif', e.target.value)} />
              </Campo>
            </div>
            {/* Fila 2: Dirección */}
            <Campo label="Dirección">
              <input style={estiloInput()} value={form.direccion} onChange={e => set('direccion', e.target.value)} />
            </Campo>
            {/* Fila 3: CP · Localidad · Provincia · País */}
            <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr 1fr' : '100px 1fr 1fr 1fr', gap: '12px' }}>
              <Campo label="CP">
                <input style={estiloInput()} value={form.cp} onChange={e => set('cp', e.target.value)} />
              </Campo>
              <Campo label="Localidad">
                <input style={estiloInput()} value={form.localidad} onChange={e => set('localidad', e.target.value)} />
              </Campo>
              <Campo label="Provincia">
                <input style={estiloInput()} value={form.provincia} onChange={e => set('provincia', e.target.value)} />
              </Campo>
              <Campo label="País">
                <input style={estiloInput()} value={form.pais} onChange={e => set('pais', e.target.value)} />
              </Campo>
            </div>
            {/* Fila 4 (particular): Móvil · Teléfono fijo · Email */}
            <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr 2fr', gap: '12px' }}>
              <Campo label="Móvil">
                <input type="tel" style={estiloInput()} value={form.movil} onChange={e => set('movil', e.target.value)} />
              </Campo>
              <Campo label="Teléfono fijo">
                <input type="tel" style={estiloInput()} value={form.telefono} onChange={e => set('telefono', e.target.value)} />
              </Campo>
              <Campo label="Email" error={errores.email}>
                <input type="email" style={estiloInput(errores.email)} value={form.email} onChange={e => set('email', e.target.value)} />
              </Campo>
            </div>
          </div>
        )}
      </div>

      {/* CONTACTO PRINCIPAL — solo empresa */}
      {form.tipo === 'empresa' && (
        <div style={{ ...styles.seccion, borderLeft: '3px solid #534AB7' }}>
          <h2 style={{ ...styles.seccionTitulo, color: '#7B6FE8' }}>Contacto principal</h2>
          <BloqueContacto prefijo="c1" form={form} set={set} errores={errores} conMobil={true} />
        </div>
      )}

      {/* CONTACTO ADMINISTRACIÓN — empresa y particular */}
      <div style={{ ...styles.seccion, borderLeft: '3px solid rgba(83,74,183,0.3)' }}>
        <h2 style={styles.seccionTitulo}>
          Contacto de administración{' '}
          <span style={{ color: '#AFA9EC', fontWeight: '400', textTransform: 'none', letterSpacing: 0, fontSize: '0.8rem' }}>(opcional)</span>
        </h2>
        <BloqueContacto prefijo="c2" form={form} set={set} errores={errores} conMobil={true} />
      </div>

      {/* DATOS FISCALES */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Datos fiscales <span style={{ color: '#AFA9EC', fontWeight: '400', textTransform: 'none', letterSpacing: 0, fontSize: '0.8rem' }}>(opcional)</span></h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Campo label="Forma de pago">
            <input style={estiloInput()} value={form.forma_pago || ''} onChange={e => set('forma_pago', e.target.value)} />
          </Campo>
          <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr', gap: '12px' }}>
            <Campo label="Banco">
              <input style={estiloInput()} value={form.banco || ''} onChange={e => set('banco', e.target.value)} />
            </Campo>
            <Campo label="IBAN">
              <input style={estiloInput()} value={form.iban || ''} onChange={e => set('iban', e.target.value)} />
            </Campo>
          </div>
        </div>
      </div>

      {/* NOTAS */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Notas iniciales</h2>
        <textarea
          style={{ ...estiloInput(), resize: 'vertical', minHeight: '80px' }}
          value={form.notas_iniciales}
          onChange={e => set('notas_iniciales', e.target.value)}
          placeholder="Observaciones sobre este cliente..."
        />
      </div>

      {/* BARRA FIJA */}
      <div style={styles.barraGuardar}>
        <button style={styles.botonSecundario} onClick={intentarCancelar}>Cancelar</button>
        <button style={styles.botonPrimario} onClick={handleGuardar} disabled={guardando}>
          {guardando ? 'Guardando...' : 'Guardar cliente'}
        </button>
      </div>
    </div>
  );
}

// ─── FICHA DE CLIENTE ─────────────────────────────────────────────────────────

function FichaCliente({ cliente, onVolver, onActualizar, verDatosEconomicos = true, esAdmin, onEliminar }) {
  // Sesión 34: si quedó un borrador sin guardar de esta ficha, se reabre en edición
  const hayBorrador = useHayBorrador(`cliente:${cliente.id}`);
  const [modoEdicion, setModoEdicion] = useState(hayBorrador);
  const [datosLocales, setDatosLocales] = useState(cliente);
  const [notas, setNotas] = useState([]);
  const [nuevaNota, setNuevaNota] = useState('');
  const [guardandoNota, setGuardandoNota] = useState(false);
  const [proyectos, setProyectos] = useState([]);
  const [modal, setModal] = useState(false);
  const [modalEliminar, setModalEliminar] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  useEffect(() => { cargarNotas(); cargarProyectos(); }, [cliente.id]);

  const cargarNotas = async () => {
    try {
      const q = query(collection(db, 'clientes', cliente.id, 'notas'), orderBy('fecha', 'desc'));
      const snap = await getDocs(q);
      setNotas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
  };

  const cargarProyectos = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'proyectos'), where('clienteId', '==', cliente.id)));
      setProyectos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
  };

  const añadirNota = async () => {
    if (!nuevaNota.trim()) return;
    setGuardandoNota(true);
    try {
      await addDoc(collection(db, 'clientes', cliente.id, 'notas'), { texto: nuevaNota.trim(), fecha: serverTimestamp() });
      setNuevaNota('');
      await cargarNotas();
    } catch (e) { console.error(e); }
    setGuardandoNota(false);
  };

  // Sesión 34: solicitud de eliminación (comercial) o su rechazo (admin)
  const cambiarSolicitud = (solicitud) => {
    const actualizado = { ...datosLocales, solicitud_eliminacion: solicitud };
    setDatosLocales(actualizado);
    onActualizar(actualizado);
  };

  const handleGuardarEdicion = async (form) => {
    try {
      await updateDoc(doc(db, 'clientes', cliente.id), { ...form, actualizado_en: serverTimestamp() });
      const actualizado = { ...datosLocales, ...form };
      setDatosLocales(actualizado);
      onActualizar(actualizado);
      setModoEdicion(false);
    } catch (e) { console.error(e); }
  };

  const eliminarCliente = async () => {
    setEliminando(true);
    try {
      // Sesión 34: borrado común (operaciones.js)
      await ejecutarEliminacion('cliente', cliente.id, datosLocales);
      onEliminar(cliente.id);
    } catch (e) { console.error(e); setEliminando(false); }
  };

  const volumenTotal = proyectos.reduce((s, p) => s + (Number(p.importe_facturado) || Number(p.importe_encargo) || 0), 0);
  const proyectosConMargen = proyectos.filter(p => p.margen !== undefined && p.margen !== null);
  const margenMedio = proyectosConMargen.length
    ? proyectosConMargen.reduce((s, p) => s + Number(p.margen), 0) / proyectosConMargen.length
    : null;

  const nombreMostrar = datosLocales.tipo === 'empresa'
    ? datosLocales.razon_social
    : `${datosLocales.nombre || ''} ${datosLocales.apellidos || ''}`.trim();

  if (modoEdicion) {
    return (
      <>
        {modal && (
          <ModalConfirmar
            mensaje="¿Quieres salir sin guardar los cambios?"
            onConfirmar={() => { setModoEdicion(false); setModal(false); }}
            onCancelar={() => setModal(false)}
          />
        )}
        <FormularioCliente
          titulo="Editar cliente"
          inicial={datosLocales}
          onGuardar={handleGuardarEdicion}
          onCancelar={() => setModoEdicion(false)}
          claveBorrador={`cliente:${cliente.id}`}
        />
      </>
    );
  }

  return (
    <div style={styles.container}>
      {modalEliminar && (
        <ModalConfirmar
          titulo="Eliminar cliente"
          mensaje={`¿Seguro que quieres eliminar a ${nombreMostrar}? Se eliminarán también sus notas. Esta acción no se puede deshacer.`}
          textoCancelar="Cancelar"
          textoConfirmar={eliminando ? 'Eliminando...' : 'Sí, eliminar'}
          onConfirmar={eliminarCliente}
          onCancelar={() => setModalEliminar(false)}
        />
      )}
      {/* Cabecera */}
      <div style={styles.cabecera}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button style={styles.botonVolver} onClick={onVolver}>← Clientes</button>
          <div>
            <h1 style={styles.titulo}>{nombreMostrar}</h1>
            {datosLocales.tipo === 'empresa' && datosLocales.nombre_comercial && datosLocales.nombre_comercial !== datosLocales.razon_social && (
              <p style={{ color: '#AFA9EC', fontSize: '0.85rem', marginTop: '2px' }}>{datosLocales.nombre_comercial}</p>
            )}
          </div>
          <span style={{ background: 'rgba(83,74,183,0.2)', color: '#7B6FE8', fontSize: '0.72rem', fontWeight: '500', padding: '3px 10px', borderRadius: '20px' }}>
            {datosLocales.tipo === 'empresa' ? 'Empresa' : 'Particular'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {esAdmin
            ? <button style={styles.botonEliminar} onClick={() => setModalEliminar(true)}>Eliminar cliente</button>
            : <BotonSolicitarEliminacion coleccion="clientes" docId={cliente.id} solicitud={datosLocales.solicitud_eliminacion} nombreRegistro={nombreMostrar} onCambio={cambiarSolicitud} />}
          <button style={styles.botonSecundario} onClick={() => setModoEdicion(true)}>Editar</button>
        </div>
      </div>
      <AvisoSolicitudEliminacion coleccion="clientes" docId={cliente.id} solicitud={datosLocales.solicitud_eliminacion} esAdmin={esAdmin}
        onCambio={cambiarSolicitud} onEliminar={() => setModalEliminar(true)} />

      {/* Contenido con padding */}
      <div style={{ padding: '24px 32px 32px' }}>
      {/* Info secundaria */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {datosLocales.nif && <span style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>NIF/CIF: <span style={{ color: '#fff' }}>{datosLocales.nif}</span></span>}
        <span style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Alta: <span style={{ color: '#fff' }}>{formatearFechaCorta(datosLocales.creado_en)}</span></span>
        <span style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>{proyectos.length} {proyectos.length === 1 ? 'proyecto' : 'proyectos'}</span>
      </div>

      {/* KPIs — solo si tiene acceso a datos económicos */}
      {verDatosEconomicos && (
        <div style={styles.gridKpis}>
          <div style={styles.kpiCard}>
            <p style={styles.kpiLabel}>Proyectos totales</p>
            <p style={{ ...styles.kpiValor, color: '#7B6FE8' }}>{proyectos.length}</p>
          </div>
          <div style={styles.kpiCard}>
            <p style={styles.kpiLabel}>Volumen facturado</p>
            <p style={{ ...styles.kpiValor, color: '#1D9E75', fontSize: '1.3rem' }}>{formatearEuros(volumenTotal)}</p>
          </div>
          <div style={styles.kpiCard}>
            <p style={styles.kpiLabel}>Margen medio</p>
            <p style={{ ...styles.kpiValor, color: colorMargen(margenMedio) }}>{formatearPct(margenMedio)}</p>
          </div>
        </div>
      )}
      {/* KPI básico si no ve datos económicos */}
      {!verDatosEconomicos && (
        <div style={{ ...styles.gridKpis, gridTemplateColumns: '1fr' }}>
          <div style={styles.kpiCard}>
            <p style={styles.kpiLabel}>Proyectos totales</p>
            <p style={{ ...styles.kpiValor, color: '#7B6FE8' }}>{proyectos.length}</p>
          </div>
        </div>
      )}

      <div style={styles.fichaGrid}>
        {/* Columna izquierda */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Contacto principal — solo empresa */}
          {datosLocales.tipo === 'empresa' && (
            <div style={{ ...styles.bloque, borderLeft: '3px solid #534AB7' }}>
              <h2 style={{ ...styles.bloqueTitulo, color: '#7B6FE8' }}>Contacto principal</h2>
              {(datosLocales.c1_nombre || datosLocales.c1_movil || datosLocales.c1_telefono || datosLocales.c1_email) ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {datosLocales.c1_nombre && <p style={{ color: '#ffffff', fontWeight: '500', fontSize: '0.95rem' }}>{datosLocales.c1_nombre}</p>}
                  {datosLocales.c1_cargo && <p style={{ color: '#AFA9EC', fontSize: '0.82rem' }}>{datosLocales.c1_cargo}</p>}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: datosLocales.c1_nombre ? '8px' : '0' }}>
                    {datosLocales.c1_movil && <a href={`tel:${datosLocales.c1_movil}`} style={{ color: '#7B6FE8', fontSize: '0.85rem' }}>📱 {datosLocales.c1_movil}</a>}
                    {datosLocales.c1_telefono && <a href={`tel:${datosLocales.c1_telefono}`} style={{ color: '#7B6FE8', fontSize: '0.85rem' }}>☎️ {datosLocales.c1_telefono}</a>}
                    {datosLocales.c1_email && <a href={`mailto:${datosLocales.c1_email}`} style={{ color: '#7B6FE8', fontSize: '0.85rem' }}>✉️ {datosLocales.c1_email}</a>}
                  </div>
                </div>
              ) : <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Sin contacto principal registrado</p>}
            </div>
          )}

          {/* Datos de contacto directo — solo particular */}
          {datosLocales.tipo === 'particular' && (datosLocales.movil || datosLocales.telefono || datosLocales.email) && (
            <div style={{ ...styles.bloque, borderLeft: '3px solid #534AB7' }}>
              <h2 style={{ ...styles.bloqueTitulo, color: '#7B6FE8' }}>Contacto</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {datosLocales.movil && <a href={`tel:${datosLocales.movil}`} style={{ color: '#7B6FE8', fontSize: '0.85rem' }}>📱 {datosLocales.movil}</a>}
                {datosLocales.telefono && <a href={`tel:${datosLocales.telefono}`} style={{ color: '#7B6FE8', fontSize: '0.85rem' }}>☎️ {datosLocales.telefono}</a>}
                {datosLocales.email && <a href={`mailto:${datosLocales.email}`} style={{ color: '#7B6FE8', fontSize: '0.85rem' }}>✉️ {datosLocales.email}</a>}
              </div>
            </div>
          )}

          {/* Contacto administración */}
          {(datosLocales.c2_nombre || datosLocales.c2_email || datosLocales.c2_movil || datosLocales.c2_telefono) ? (
            <div style={{ ...styles.bloque, borderLeft: '3px solid rgba(83,74,183,0.3)' }}>
              <h2 style={styles.bloqueTitulo}>Contacto de administración</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {datosLocales.c2_nombre && <p style={{ color: '#ffffff', fontWeight: '500', fontSize: '0.95rem' }}>{datosLocales.c2_nombre}</p>}
                {datosLocales.c2_cargo && <p style={{ color: '#AFA9EC', fontSize: '0.82rem' }}>{datosLocales.c2_cargo}</p>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: datosLocales.c2_nombre ? '8px' : '0' }}>
                  {datosLocales.c2_movil && <a href={`tel:${datosLocales.c2_movil}`} style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>📱 {datosLocales.c2_movil}</a>}
                  {datosLocales.c2_telefono && <a href={`tel:${datosLocales.c2_telefono}`} style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>☎️ {datosLocales.c2_telefono}</a>}
                  {datosLocales.c2_email && <a href={`mailto:${datosLocales.c2_email}`} style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>✉️ {datosLocales.c2_email}</a>}
                </div>
              </div>
            </div>
          ) : null}

          {/* Datos fiscales */}
          {(datosLocales.iban || datosLocales.banco || datosLocales.forma_pago) && (
            <div style={styles.bloque}>
              <h2 style={styles.bloqueTitulo}>Datos fiscales</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {datosLocales.forma_pago && <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Forma de pago: <span style={{ color: '#fff' }}>{datosLocales.forma_pago}</span></p>}
                {datosLocales.banco && <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Banco: <span style={{ color: '#fff' }}>{datosLocales.banco}</span></p>}
                {datosLocales.iban && <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>IBAN: <span style={{ color: '#fff', fontFamily: 'monospace' }}>{datosLocales.iban}</span></p>}
              </div>
            </div>
          )}

          {/* Dirección fiscal */}
          {(datosLocales.direccion || datosLocales.localidad) && (
            <div style={styles.bloque}>
              <h2 style={styles.bloqueTitulo}>Dirección fiscal</h2>
              <p style={{ color: '#ffffff', fontSize: '0.88rem', lineHeight: '1.6' }}>
                {[datosLocales.direccion, datosLocales.cp, datosLocales.localidad, datosLocales.provincia, datosLocales.pais].filter(Boolean).join(', ')}
              </p>
            </div>
          )}
        </div>

        {/* Columna derecha */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Proyectos */}
          <div style={styles.bloque}>
            <h2 style={styles.bloqueTitulo}>Proyectos</h2>
            {proyectos.length === 0 ? (
              <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Sin proyectos asociados</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.tabla}>
                    <thead>
                      <tr>
                        {['Código', 'Sector', 'Estado', 'Encargo', ...(verDatosEconomicos ? ['Margen'] : [])].map(h => <th key={h} style={styles.th}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {proyectos.map(p => (
                        <tr key={p.id} style={{ borderBottom: '1px solid rgba(83,74,183,0.1)' }}>
                          <td style={{ ...styles.td, color: '#7B6FE8', fontWeight: '500' }}>{p.codigo || '—'}</td>
                          <td style={styles.td}>{p.sector || '—'}</td>
                          <td style={styles.td}>
                            <span style={{ background: 'rgba(83,74,183,0.2)', color: '#AFA9EC', fontSize: '0.72rem', padding: '2px 8px', borderRadius: '20px' }}>{p.estado || '—'}</span>
                          </td>
                          <td style={styles.td}>{formatearEuros(p.importe_encargo)}</td>
                          {verDatosEconomicos && (
                            <td style={{ ...styles.td, color: colorMargen(p.margen), fontWeight: '500' }}>
                              {p.margen !== undefined && p.margen !== null ? formatearPct(p.margen) : '—'}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
              </div>
            )}
          </div>

          {/* Notas */}
          <div style={styles.bloque}>
            <h2 style={styles.bloqueTitulo}>Notas</h2>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <textarea
                value={nuevaNota}
                onChange={e => setNuevaNota(e.target.value)}
                placeholder="Añadir una nota..."
                rows={2}
                style={{ ...estiloInput(), resize: 'none', flex: 1 }}
              />
              <button
                onClick={añadirNota}
                disabled={guardandoNota || !nuevaNota.trim()}
                style={{ ...styles.botonPrimario, alignSelf: 'stretch', padding: '0 16px', opacity: !nuevaNota.trim() ? 0.5 : 1 }}
              >
                {guardandoNota ? '...' : 'Añadir'}
              </button>
            </div>
            {notas.length === 0 ? (
              <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Sin notas</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {notas.map(n => (
                  <div key={n.id} style={{ background: '#0e0b24', borderRadius: '8px', padding: '12px 14px', borderLeft: '3px solid rgba(83,74,183,0.3)' }}>
                    <p style={{ color: '#ffffff', fontSize: '0.88rem', marginBottom: '6px' }}>{n.texto}</p>
                    <span style={{ color: '#AFA9EC', fontSize: '0.75rem' }}>{formatearFechaCorta(n.fecha)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

// ─── MÓDULO PRINCIPAL — LISTADO ───────────────────────────────────────────────

export default function Clientes({ esAdmin, verDatosEconomicos = true, accionInicial, onAccionCompletada }) {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Sesión 32 · Entrega B: los clientes viven en el almacén compartido (una descarga por sesión)
  const { items: clientes, setItems: setClientes, cargando } = useColeccion('clientes');
  // Sesión 34: si se salió del módulo con un borrador sin guardar, se vuelve a él
  const borradorPendiente = useBorradorPendiente('cliente');
  const [vista, setVista] = useState(borradorPendiente === 'nuevo' && !accionInicial ? 'nuevo' : 'listado');
  const [clienteActivo, setClienteActivo] = useState(null);
  const borradorAbierto = React.useRef(false);
  useEffect(() => {
    if (borradorAbierto.current || !borradorPendiente || borradorPendiente === 'nuevo' || cargando || accionInicial) return;
    borradorAbierto.current = true;
    const c = clientes.find(x => x.id === borradorPendiente);
    if (c) { setClienteActivo(c); setVista('ficha'); }
  }, [borradorPendiente, cargando, clientes, accionInicial]);
  // Búsqueda, filtros, orden, filas visibles y scroll se recuerdan durante la sesión
  const [busqueda, setBusqueda] = useEstadoPersistente('clientes.busqueda', '');
  const [filtroTipo, setFiltroTipo] = useEstadoPersistente('clientes.filtroTipo', '');
  const [ordenacion, setOrdenacion] = useEstadoPersistente('clientes.ordenacion', 'nombre_asc');
  const [visibles, setVisibles] = useEstadoPersistente('clientes.visibles', 50);
  usePosicionScroll('clientes', vista === 'listado' && !cargando);

  // Sesión 34: abrir una ficha desde el Dashboard (solicitudes de eliminación)
  useEffect(() => {
    if (!accionInicial) return;
    if (accionInicial.accion === 'ficha' && accionInicial.id) {
      if (cargando) return;
      const c = clientes.find(x => x.id === accionInicial.id);
      if (c) { setClienteActivo(c); setVista('ficha'); }
      if (onAccionCompletada) onAccionCompletada();
    }
  }, [accionInicial, clientes, cargando]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNuevoCliente = async (form) => {
    try {
      const docRef = await addDoc(collection(db, 'clientes'), { ...form, creado_en: serverTimestamp() });
      if (form.notas_iniciales?.trim()) {
        await addDoc(collection(db, 'clientes', docRef.id, 'notas'), { texto: form.notas_iniciales.trim(), fecha: serverTimestamp() });
      }
      // getDoc para obtener timestamps reales — REGLA PERMANENTE
      const snap = await getDoc(docRef);
      const nuevo = { id: snap.id, ...snap.data() };
      setClientes(prev => [nuevo, ...prev]);
      setVista('listado');
    } catch (e) { console.error(e); }
  };

  const handleActualizarCliente = (actualizado) => {
    setClientes(prev => prev.map(c => c.id === actualizado.id ? actualizado : c));
    setClienteActivo(actualizado);
  };

  const onEliminarCliente = (id) => {
    setClientes(prev => prev.filter(c => c.id !== id));
    setVista('listado');
  };

  const nombreCliente = (c) => c.tipo === 'empresa'
    ? (c.razon_social || '').toLowerCase()
    : `${c.nombre || ''} ${c.apellidos || ''}`.toLowerCase().trim();

  const clientesFiltrados = clientes
    .filter(c => {
      const hayBusqueda = norm(busqueda);
      const matchBusqueda = !busqueda ||
        norm(nombreCliente(c)).includes(hayBusqueda) ||
        norm(c.nombre_comercial).includes(hayBusqueda) ||
        norm(c.c1_nombre).includes(hayBusqueda) ||
        norm(c.nif).includes(hayBusqueda) ||
        norm(c.localidad).includes(hayBusqueda) ||
        (c.c1_movil || '').includes(busqueda) ||
        (c.c1_telefono || '').includes(busqueda) ||
        (c.movil || '').includes(busqueda) ||
        (c.telefono || '').includes(busqueda);
      const matchTipo = !filtroTipo || c.tipo === filtroTipo;
      return matchBusqueda && matchTipo;
    })
    .sort((a, b) => {
      if (ordenacion === 'nombre_asc') return nombreCliente(a).localeCompare(nombreCliente(b), 'es');
      if (ordenacion === 'nombre_desc') return nombreCliente(b).localeCompare(nombreCliente(a), 'es');
      if (ordenacion === 'fecha_desc') return (b.creado_en?.seconds || 0) - (a.creado_en?.seconds || 0);
      if (ordenacion === 'fecha_asc') return (a.creado_en?.seconds || 0) - (b.creado_en?.seconds || 0);
      return 0;
    });

  const hayFiltros = busqueda || filtroTipo;
  const totalEmpresas = clientes.filter(c => c.tipo === 'empresa').length;
  const totalParticulares = clientes.filter(c => c.tipo === 'particular').length;
  const clientesVisibles = clientesFiltrados.slice(0, visibles);

  if (vista === 'nuevo') return <FormularioCliente titulo="Nuevo cliente" onGuardar={handleNuevoCliente} onCancelar={() => setVista('listado')} claveBorrador="cliente:nuevo" />;
  if (vista === 'ficha' && clienteActivo) return (
    <FichaCliente
      cliente={clienteActivo}
      onVolver={() => { setVista('listado'); }}
      onActualizar={handleActualizarCliente}
      verDatosEconomicos={verDatosEconomicos}
      esAdmin={esAdmin}
      onEliminar={onEliminarCliente}
    />
  );

  return (
    <div style={styles.container}>
      <div style={styles.cabecera}>
        <div>
          <h1 style={styles.titulo}>Clientes</h1>
          <p style={{ color: '#AFA9EC', fontSize: '0.85rem', marginTop: '4px' }}>
            {clientes.length} clientes · {totalEmpresas} empresas · {totalParticulares} particulares
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <BarraActualizacion colecciones={['clientes']} />
          <button style={styles.botonPrimario} onClick={() => setVista('nuevo')}>+ Nuevo cliente</button>
        </div>
      </div>

      <div style={styles.contenido}>
      {/* Filtros */}
      <div style={styles.filtrosBar}>
        <input
          style={styles.buscador}
          placeholder="Buscar por nombre, NIF, contacto, teléfono o localidad..."
          value={busqueda}
          onChange={e => { setBusqueda(e.target.value); setVisibles(50); }}
        />
        <div style={styles.pills}>
          {[{ v: 'empresa', l: 'Empresas' }, { v: 'particular', l: 'Particulares' }].map(({ v, l }) => (
            <button key={v} style={{
              ...styles.pill,
              background: filtroTipo === v ? '#534AB7' : 'transparent',
              color: filtroTipo === v ? '#fff' : '#AFA9EC',
              borderColor: filtroTipo === v ? '#534AB7' : 'rgba(83,74,183,0.3)',
            }} onClick={() => { setFiltroTipo(filtroTipo === v ? '' : v); setVisibles(50); }}>{l}</button>
          ))}
        </div>
        <select style={styles.select} value={ordenacion} onChange={e => setOrdenacion(e.target.value)}>
          <option value="nombre_asc">Nombre A → Z</option>
          <option value="nombre_desc">Nombre Z → A</option>
          <option value="fecha_desc">Más recientes</option>
          <option value="fecha_asc">Más antiguos</option>
        </select>
        {hayFiltros && (
          <button style={styles.botonLimpiar} onClick={() => { setBusqueda(''); setFiltroTipo(''); setVisibles(50); }}>Limpiar filtros</button>
        )}
      </div>

      {/* Tabla */}
      {cargando ? (
        <p style={{ color: '#AFA9EC', padding: '32px 0' }}>Cargando...</p>
      ) : clientesFiltrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#AFA9EC' }}>
          {clientes.length === 0 ? 'Aún no hay clientes registrados.' : 'No hay clientes que coincidan con la búsqueda.'}
        </div>
      ) : (
        <div style={styles.tablaWrapper}>
          <table style={styles.tabla}>
            <thead>
              <tr>{['Nombre / Razón social', 'Tipo', 'NIF / CIF', 'Contacto principal', 'Localidad', 'Alta'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {clientesVisibles.map(c => {
                const nombre = c.tipo === 'empresa'
                  ? c.razon_social
                  : `${c.nombre || ''} ${c.apellidos || ''}`.trim();
                const contactoNombre = c.tipo === 'empresa'
                  ? c.c1_nombre
                  : `${c.nombre || ''} ${c.apellidos || ''}`.trim() || null;
                const contactoCargo = c.tipo === 'empresa' ? c.c1_cargo : null;
                return (
                  <tr key={c.id} style={styles.tr}
                    onClick={() => { setClienteActivo(c); setVista('ficha'); }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(83,74,183,0.06)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={styles.td}><span style={styles.nombreLead}>{nombre || '—'}</span></td>
                    <td style={styles.td}>
                      <span style={{
                        background: c.tipo === 'empresa' ? 'rgba(83,74,183,0.2)' : 'rgba(186,117,23,0.15)',
                        color: c.tipo === 'empresa' ? '#7B6FE8' : '#BA7517',
                        fontSize: '0.72rem', fontWeight: '500', padding: '3px 10px', borderRadius: '20px',
                      }}>
                        {c.tipo === 'empresa' ? 'Empresa' : 'Particular'}
                      </span>
                    </td>
                    <td style={{ ...styles.td, color: '#AFA9EC' }}>{c.nif || '—'}</td>
                    <td style={styles.td}>
                      {contactoNombre
                        ? <><span style={styles.nombreLead}>{contactoNombre}</span>{contactoCargo && <span style={styles.subLead}>{contactoCargo}</span>}</>
                        : <span style={{ color: '#AFA9EC' }}>—</span>}
                    </td>
                    <td style={{ ...styles.td, color: '#AFA9EC' }}>{c.localidad || '—'}</td>
                    <td style={{ ...styles.td, color: '#AFA9EC' }}>{formatearFechaCorta(c.creado_en)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {clientesFiltrados.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          {visibles < clientesFiltrados.length && (
            <button style={{ ...styles.botonSecundario, marginBottom: '12px' }} onClick={() => setVisibles(v => v + 50)}>
              Mostrar 50 más
            </button>
          )}
          <p style={{ color: '#AFA9EC', fontSize: '0.8rem' }}>
            Mostrando {Math.min(visibles, clientesFiltrados.length)} de {clientesFiltrados.length} — usa el buscador para filtrar
          </p>
        </div>
      )}
      </div>
    </div>
  );
}

// ─── ESTILOS ──────────────────────────────────────────────────────────────────

const styles = {
  container: { padding: '0', maxWidth: '100%' },
  cabecera: { position: 'sticky', top: 0, zIndex: 50, background: '#0e0b24', borderBottom: '1px solid rgba(83,74,183,0.2)', padding: '20px 32px 12px', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' },
  contenido: { padding: '24px 32px 32px' },
  titulo: { color: '#ffffff', fontSize: '1.6rem', fontWeight: '300', letterSpacing: '0.02em' },
  botonPrimario: { background: '#534AB7', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', fontWeight: '500', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonSecundario: { background: 'transparent', color: '#AFA9EC', border: '1px solid rgba(83,74,183,0.4)', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonEliminar: { background: 'transparent', color: '#D85A30', border: '1px solid #D85A30', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonVolver: { background: 'transparent', color: '#7B6FE8', border: 'none', fontSize: '0.88rem', cursor: 'pointer', padding: '0', fontFamily: 'DM Sans, sans-serif' },
  botonLimpiar: { background: 'transparent', color: '#D85A30', border: '1px solid #D85A30', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  barraGuardar: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1a1440', borderTop: '1px solid rgba(83,74,183,0.3)', padding: '16px 32px', display: 'flex', justifyContent: 'flex-end', gap: '12px', zIndex: 100 },
  gridKpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' },
  kpiCard: { background: '#1a1440', borderRadius: '10px', padding: '16px 20px', border: '1px solid rgba(83,74,183,0.2)' },
  kpiLabel: { color: '#AFA9EC', fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' },
  kpiValor: { color: '#ffffff', fontSize: '1.6rem', fontWeight: '300' },
  filtrosBar: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px', alignItems: 'center' },
  buscador: { background: '#1a1440', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', padding: '9px 14px', color: '#fff', fontSize: '0.88rem', outline: 'none', minWidth: '240px', flex: '1', fontFamily: 'DM Sans, sans-serif' },
  pills: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  pill: { border: '1px solid', borderRadius: '20px', padding: '6px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', background: 'transparent' },
  select: { background: '#1a1440', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', padding: '9px 14px', color: '#AFA9EC', fontSize: '0.88rem', outline: 'none', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  tablaWrapper: { overflowX: 'auto' },
  tabla: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#AFA9EC', fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '10px 14px', textAlign: 'left', borderBottom: '1px solid rgba(83,74,183,0.2)' },
  tr: { borderBottom: '1px solid rgba(83,74,183,0.1)', cursor: 'pointer' },
  td: { padding: '12px 14px', color: '#ffffff', fontSize: '0.88rem', verticalAlign: 'middle' },
  nombreLead: { display: 'block', fontWeight: '500' },
  subLead: { display: 'block', color: '#AFA9EC', fontSize: '0.78rem' },
  fichaGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' },
  bloque: { background: '#1a1440', borderRadius: '12px', padding: '24px', border: '1px solid rgba(83,74,183,0.2)' },
  bloqueTitulo: { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' },
  datosGrid: { display: 'flex', flexDirection: 'column', gap: '10px' },
  datoFila: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  datoLabel: { color: '#AFA9EC', fontSize: '0.82rem', flexShrink: 0 },
  datoValor: { color: '#ffffff', fontSize: '0.88rem', textAlign: 'right' },
  seccion: { background: '#1a1440', borderRadius: '12px', padding: '24px', border: '1px solid rgba(83,74,183,0.2)', marginBottom: '16px' },
  seccionTitulo: { color: '#ffffff', fontSize: '0.9rem', fontWeight: '500', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid rgba(83,74,183,0.2)', textTransform: 'none', letterSpacing: 'normal' },
  label: { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.04em' },
};
